import { AppUrls } from "../../ts/app";
import { Controller } from "../../ts/controller";
import { Logger } from "../../ts/utils/logger";

enum WizardStep {
    forgot = 0,
    reset = 1,
    success = 2,
}

export class ForgotPasswordPageController extends Controller {
    wizardStep: WizardStep = WizardStep.forgot;
    loadingForgot: boolean = false;
    loadingReset: boolean = false;
    errors: string[] = [];
    username: string | null = null;
    passwordsVisible: boolean = false;

    constructor() {
        super({
            logLabel: 'ForgotPasswordPageController',
        });
    }

    public initialize(): void {
        this.bindClick({
            elementId: 'forgot-submit-button',
            handler: this._submitForgotPassword.bind(this),
        });
        this.bindClick({
            elementId: 'reset-submit-button',
            handler: this._submitResetPassword.bind(this),
        });
        this.bindClick({
            elementId: 'toggle-reset-password-visibility',
            handler: () => {
                this.passwordsVisible = !this.passwordsVisible;
                this._setState();
            },
        });

        this._setState();
        Logger.info(this.logLabel, 'Initialized');
    }

    private _setState(): void {
        const forgotSection = this.getElementByIdOrWarn('forgot-password-section');
        const resetSection = this.getElementByIdOrWarn('reset-password-section');
        const successSection = this.getElementByIdOrWarn('reset-success-section');
        const displayUsernameElement = this.getElementByIdOrWarn('display-username');
        const forgotSubmitButton = this.getElementByIdOrWarn<HTMLButtonElement>('forgot-submit-button');
        const resetSubmitButton = this.getElementByIdOrWarn<HTMLButtonElement>('reset-submit-button');

        if (forgotSection) forgotSection.style.display = 'none';
        if (resetSection) resetSection.style.display = 'none';
        if (successSection) successSection.style.display = 'none';

        switch (this.wizardStep) {
            case WizardStep.forgot:
                if (forgotSection) {
                    forgotSection.style.display = 'flex';
                }
                this._setButtonLoadingState(forgotSubmitButton!, this.loadingForgot);
                this._setErrorsState({ errorElementId: 'forgot-error-message' });
                break;

            case WizardStep.reset:
                if (resetSection) resetSection.style.display = 'flex';
                if (displayUsernameElement) {
                    displayUsernameElement.textContent = this.username;
                }
                this._setButtonLoadingState(resetSubmitButton!, this.loadingReset);
                this._setErrorsState({ errorElementId: 'reset-error-message' });
                this._setPasswordVisibilityState();
                break;

            case WizardStep.success:
                if (successSection) successSection.style.display = 'flex';
                break;

            default:
                Logger.warn(this.logLabel, `Unknown wizard step: ${this.wizardStep}`);
                break;
        }
    }

    private _setButtonLoadingState(
        button: HTMLButtonElement,
        loading: boolean,
    ): void {
        if (!button) return;
        const buttonContent = button.querySelector('.button-content') as HTMLElement;
        const buttonSpinner = button.querySelector('.button-spinner') as HTMLElement;
        if (buttonContent && buttonSpinner) {
            buttonContent.style.display = loading ? 'none' : 'block';
            buttonSpinner.style.display = loading ? 'block' : 'none';
        }
        button.disabled = loading;
        button.style.opacity = loading ? '0.6' : '1';
        button.style.cursor = loading ? 'not-allowed' : 'pointer';
    }

    private _setErrorsState({
        errorElementId,
    }: {
        errorElementId: string,
    }): void {
        const errorElement = this.getElementByIdOrWarn(errorElementId);
        if (!errorElement) return;
        if (this.errors.length > 0) {
            errorElement.innerHTML = this.errors.join('<br>');
            errorElement.style.display = 'block';
        } else {
            errorElement.style.display = 'none';
            errorElement.innerHTML = '';
        }
    }

    private _setPasswordVisibilityState(): void {
        const passwordField = this.getElementByIdOrWarn<HTMLInputElement>('reset-password');
        const confirmPasswordField = this.getElementByIdOrWarn<HTMLInputElement>('reset-password-confirm');
        const togglePasswordButton = this.getElementByIdOrWarn<HTMLButtonElement>('toggle-reset-password-visibility');
        if (!passwordField || !confirmPasswordField || !togglePasswordButton) {
            return;
        }
        const type = this.passwordsVisible ? 'text' : 'password';
        passwordField.type = type;
        confirmPasswordField.type = type;
        togglePasswordButton.textContent = this.passwordsVisible ? 'Hide' : 'Show';
    }

    private async _submitForgotPassword(): Promise<void> {
        this.errors = [];

        const username = this.getElementByIdOrWarn<HTMLInputElement>('forgot-username')?.value.trim();
        if (!username) {
            this.errors.push("Username is required.");
            this._setState();
            return;
        }

        this.loadingForgot = true;
        this._setState();
        try {
            const response = await fetch(AppUrls.forgotPasswordEndpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username }),
            });

            if (response.ok) {
                this.wizardStep = WizardStep.reset;
                this.errors = [];
                this.username = username;
            } else {
                const data = await response.json();
                this.errors = this.parseErrors(data);
            }
        } catch (error) {
            Logger.error(this.logLabel, `Forgot password request failed: ${error}`);
            this.errors = ["An error occurred. Please try again."];
        } finally {
            this.loadingForgot = false;
            this._setState();
        }
    }

    private async _submitResetPassword(): Promise<void> {
        this.errors = [];

        const code = this.getElementByIdOrWarn<HTMLInputElement>('reset-code')?.value.trim();
        const password = this.getElementByIdOrWarn<HTMLInputElement>('reset-password')?.value.trim();
        const confirmPassword = this.getElementByIdOrWarn<HTMLInputElement>('reset-password-confirm')?.value.trim();

        if (!code || !password || !confirmPassword) {
            this.errors.push("All fields are required.");
            this._setState();
            return;
        }

        if (password !== confirmPassword) {
            this.errors.push("Passwords do not match.");
            this._setState();
            return;
        }

        this.loadingReset = true;
        this._setState();
        try {
            const response = await fetch(AppUrls.resetPasswordEndpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: this.username, code, password }),
            });

            if (response.ok) {
                this.wizardStep = WizardStep.success;
                this.errors = [];
            } else {
                const data = await response.json();
                this.errors = this.parseErrors(data);
            }
        } catch (error) {
            Logger.error(this.logLabel, `Reset password request failed ${error}`);
            this.errors = ["An error occurred. Please try again."];
        } finally {
            this.loadingReset = false;
            this._setState();
        }
    }
}
